import { useQuery, UseQueryResult, keepPreviousData } from '@tanstack/react-query'
import { authService } from '../services/authService'
import type { User } from '../types/milestone.types'

/**
 * Check if user might be authenticated (sessionStorage hint)
 * SessionStorage clears on browser close, persists on refresh
 */
function mightHaveSession(): boolean {
  // Check if OAuth callback just succeeded
  const urlParams = new URLSearchParams(window.location.search)
  if (urlParams.get('success') === 'true') {
    return true
  }

  // Check sessionStorage hint (set after successful login)
  return sessionStorage.getItem('isAuthenticated') === 'true'
}

/**
 * React Query hook for current user (cookie-based auth with sessionStorage hint)
 *
 * NOTE: This hook is a backward-compatible wrapper for components that need
 * React Query's return type ({ data, isLoading, error }). For new components,
 * consider using useAuth() instead for the full auth state machine.
 *
 * Both hooks share the same query key ['currentUser'] and cache.
 * Service initialization (token refresh, idle detection) is handled by useAuth.
 *
 * Features:
 * - Cookie-based authentication: Reads httpOnly cookies (rp_id_token)
 * - SessionStorage hint: Only calls /me when we think there's a session
 * - Moderate caching: 5 minute stale time, 30 minute cache time
 * - Automatic background refetch after staleness
 * - Shared cache across all components
 * - Graceful handling of unauthenticated state (returns null, not error)
 * - Prevents login page flash during navigation with placeholderData
 *
 * @returns React Query result with user data (null when not logged in)
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data: user, isLoading } = useCurrentUser()
 *
 *   if (isLoading) return <Spin />
 *   if (!user) return <div>Please log in</div>
 *
 *   return <div>Hello, {user.email}</div>
 * }
 * ```
 */
export function useCurrentUser(): UseQueryResult<User | null, Error> {
  const shouldFetch = mightHaveSession()

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await authService.getUserInfo()

      // 401 means session expired or never existed
      if (!response.success) {
        // Only clear sessionStorage for explicit auth failures (401)
        // NOT for network errors or temporary failures - keeps session hint sticky
        const isAuthError = response.errors?.some(e =>
          e.code === 'UNAUTHORIZED' || e.code === 'GET_USER_INFO_FAILED'
        )
        if (isAuthError) {
          sessionStorage.removeItem('isAuthenticated')
          console.log('📭 No active session - cleared hint')
        } else {
          console.log('📭 /me failed but keeping session hint (may be temporary)')
        }
        return null
      }

      if (!response.data) {
        console.warn('⚠️ getUserInfo succeeded but returned no data')
        // Don't clear session hint here - let explicit logout handle it
        return null
      }

      // Success! Set sessionStorage hint for future page loads
      sessionStorage.setItem('isAuthenticated', 'true')

      // NOTE: Service initialization (token refresh, idle detection) is now
      // handled by useAuth hook. This hook just reads/caches the user data.

      return response.data
    },
    enabled: shouldFetch,                // CRITICAL: Only run if we think there's a session
    staleTime: 1000 * 60 * 5,            // 5 minutes - user info changes occasionally
    gcTime: 1000 * 60 * 30,              // 30 minutes - keep in cache while active
    refetchOnWindowFocus: true,          // Refetch when user returns to tab (check session)
    refetchOnMount: true,                // Always check on component mount
    retry: false,                        // Don't retry - let OAuth callback trigger refetch
    retryOnMount: true,                  // Retry when component remounts (after OAuth)
    placeholderData: keepPreviousData,   // CRITICAL: Prevents login flash during navigation
  })
}

export default useCurrentUser
